import { useState, useEffect, useRef } from 'react';
import { RestaurantDetailsInterface } from '../../../types/RestaurantInterface';
import { BlobWrapper } from '../../../types/MediaInterface';
import { AlbumMediaInterface } from '../../../types/AlbumMediaInterface';
import { RestaurantImages } from './restaurantWizardTypes';

const DEFAULT_RESTAURANT_IMAGES: RestaurantImages = {
  logoImage: null,
  thumbnailImage: null,
  profileImages: null,
  menuCoverImage: null,
  galleryImages: null
};

const useRestaurantImages = (isEdit: boolean, currentRestaurant: RestaurantDetailsInterface | null) => {
  const [restaurantImages, setRestaurantImages] = useState<RestaurantImages>(DEFAULT_RESTAURANT_IMAGES);
  const imagesInitialized = useRef(false);

  useEffect(() => {
    if (isEdit && currentRestaurant && !imagesInitialized.current) {
      imagesInitialized.current = true;
      setRestaurantImages({
        logoImage: currentRestaurant.images?.logo?.imageURL || null,
        thumbnailImage: currentRestaurant.images?.thumbnail?.imageURL || null,
        profileImages:
          currentRestaurant.images?.profile?.map((image) => ({
            mediaID: image.imageID,
            mediaURL: image.imageURL,
            type: 'image'
          })) || [],
        menuCoverImage: currentRestaurant.images?.menuCover?.imageURL || null,
        galleryImages:
          currentRestaurant.images?.albums?.[0]?.media?.map((_media) => ({
            mediaURL: _media.mediaURL,
            mediaID: _media.mediaID,
            type: 'image'
          })) || []
      });
    }
  }, [isEdit, currentRestaurant]);

  const updateImage = (key: keyof RestaurantImages) => (value: RestaurantImages[typeof key]) =>
    setRestaurantImages((prev) => ({ ...prev, [key]: value }));

  return {
    restaurantImages,
    handleLogoImageUpload: updateImage('logoImage'),
    handleThumbnailImageUpload: updateImage('thumbnailImage'),
    handleProfileImagesUpload: updateImage('profileImages') as (files: (BlobWrapper | AlbumMediaInterface)[]) => void,
    handleMenuCoverImageUpload: updateImage('menuCoverImage'),
    handleGalleryImagesUpload: updateImage('galleryImages') as (files: (BlobWrapper | AlbumMediaInterface)[]) => void
  };
};

export default useRestaurantImages;
